import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './sidebar';
import { Header } from './header';
import api from '@/services/api';
import { setRuntimeConfig } from '@/lib/runtime-config';
import type { IApiResponse } from '@/types';

interface RuntimeSystemConfig {
  currency?: string;
  language?: string;
  timezone?: string;
}

function useHydrateRuntimeConfig() {
  const { data } = useQuery({
    queryKey: ['system-config', 'runtime'],
    queryFn: async () => {
      const { data } = await api.get<IApiResponse<RuntimeSystemConfig>>(
        '/admin/system-config/runtime'
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  useEffect(() => {
    if (data?.data) {
      setRuntimeConfig(data.data);
    }
  }, [data]);
}

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard/ejecutivo': 'Dashboard Ejecutivo',
  '/dashboard/operativo': 'Dashboard Operativo',
  '/dashboard/contable': 'Dashboard Contable',
  '/activos': 'Activos',
  '/activos/nuevo': 'Registrar Activo',
  '/activos/importar': 'Importar Activos',
  '/rfid': 'RFID',
  '/rfid/asignar': 'Asignar Tags',
  '/rfid/inventario': 'Inventario RFID',
  '/rfid/dispositivos': 'Dispositivos RFID',
  '/trazabilidad/movimientos': 'Movimientos',
  '/trazabilidad/traslados': 'Traslados',
  '/trazabilidad/mapa': 'Mapa de Activos',
  '/depreciacion': 'Depreciación',
  '/depreciacion/calcular': 'Calcular Depreciación',
  '/mantenimiento/ordenes': 'Órdenes de Trabajo',
  '/mantenimiento/planes': 'Planes de Mantenimiento',
  '/auditoria/log': 'Registro de Auditoría',
  '/auditoria/conciliacion': 'Conciliación',
  '/reportes': 'Reportes',
  '/admin/usuarios': 'Usuarios',
  '/admin/ubicaciones': 'Ubicaciones',
  '/admin/categorias': 'Categorías',
  '/admin/configuracion': 'Configuración del Sistema',
};

function getBreadcrumbs(pathname: string): Array<{ label: string; href?: string }> {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; href?: string }> = [
    { label: 'Inicio', href: '/' },
  ];

  let path = '';
  for (const segment of segments) {
    path += `/${segment}`;
    const title = ROUTE_TITLES[path];
    if (title) {
      crumbs.push({ label: title, href: path });
    }
  }

  return crumbs;
}

export function AppLayout() {
  useHydrateRuntimeConfig();
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname);
  const pageTitle =
    ROUTE_TITLES[location.pathname] || 'Asset Track 360';

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        {/* Breadcrumbs */}
        <div className="border-b bg-muted/30 px-6 py-2">
          <nav aria-label="Migas de pan">
            <ol className="flex items-center gap-1 text-sm text-muted-foreground">
              {breadcrumbs.map((crumb, index) => (
                <li key={crumb.label} className="flex items-center gap-1">
                  {index > 0 && <span aria-hidden="true">/</span>}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="font-medium text-foreground" aria-current="page">
                      {crumb.label}
                    </span>
                  ) : (
                    <a
                      href={crumb.href}
                      className="hover:text-foreground transition-colors"
                    >
                      {crumb.label}
                    </a>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>
        {/* Page content */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto p-6"
          role="main"
          aria-label={pageTitle}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
